import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';

import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';
import Switch from 'components/FormFields/Switch';
import FormSelectField from 'components/FormFields/FormSelectField';

import { BankAccountsStore } from 'stores/workflowStores/finances/bankAccounts/bankAccountsStore';
import {
  initialValues,
  BankAccount,
  BankDataOptions,
  BankAccountOwnership,
} from 'stores/workflowStores/finances/bankAccounts/model';
import { Currency } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  currency: Currency;
  bankAccountId?: string;
  refresh: () => void;
  options: BankDataOptions;
  legal?: boolean;
  accounts: number;
}

interface Stores {
  bankAccountsStore: BankAccountsStore;
}

type Props = OwnProps & Stores;

const BankAccountModal: React.FC<Props> = ({
  bankAccountId,
  opened,
  handleClose,
  bankAccountsStore,
  currency,
  refresh,
  options,
  legal,
  accounts,
}) => {
  const { t } = useTranslation();
  let formSubmitting = false;
  const formKey = 'BankAccountForm';
  const { fetchBankAccount, handleSubmit, handlePatchSubmit, data } = bankAccountsStore;
  const [AMLinfo, setAMLinfo] = useState(false);

  const isKnownToOcp = !!bankAccountId && data?.knownToOcp;
  const disabledProps = isKnownToOcp ? { disabled: true } : {};

  useEffect(() => {
    if (opened && bankAccountId) {
      fetchBankAccount(bankAccountId);
    }
  }, [bankAccountId, fetchBankAccount, opened]);

  const onSubmit = async (values: BankAccount, formikHelpers: FormikHelpers<BankAccount>) => {
    if (isKnownToOcp) {
      // If knownToOcp is true, execute the patch submit logic.
      const ok = await handlePatchSubmit(values, formikHelpers); // Patch submit logic
      if (ok) {
        handleClose();
        refresh();
      }
    } else {
      // Else execute the default submit logic.
      const ok = await handleSubmit(values, formikHelpers); // Normal submit logic
      if (ok) {
        handleClose();
        refresh();
      }
    }
  };

  useEffect(() => {
    initialValues.ownership = BankAccountOwnership.NONE;
    initialValues.primary = false;
    initialValues.currency = currency === Currency.CZK ? Currency.CZK : Currency.NONE;
    initialValues.hasIban = currency === Currency.EUR;

    if (bankAccountId === undefined && opened) {
      if (legal) {
        initialValues.ownership = BankAccountOwnership.CLIENT;
      }
      initialValues.primary = accounts === 0;
    }
  }, [bankAccountId, opened, legal, accounts, currency]);

  return options.czCodes && options.skCodes ? (
    <>
      <ConfirmDialog
        isOpen={AMLinfo}
        noHeader
        maxWidth="md"
        confirm={() => setAMLinfo(false)}
        btnConfirmText="general_agreed"
      >
        {t('bankAccount_AML_info')}
      </ConfirmDialog>

      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={handleClose}>{t('bankAccount_set')}</DialogHeader>
        <DialogContent>
          <Formik
            initialValues={data && bankAccountId ? merge({}, initialValues, data) : initialValues}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;

              return (
                <FormWithoutEnterSubmit id={t(formKey)}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Switch
                        name="hasIban"
                        label={t('bankAccount_hasIban')}
                        afterChange={() => {
                          if (formikProps.values.currency !== currency) {
                            formikProps.setFieldValue('primary', false);
                          }
                        }}
                        {...disabledProps}
                      />
                    </Grid>
                    <Grid item xs={6} />
                    <Grid item xs={6}>
                      <FormSelectField
                        name="currency"
                        label={t('general_accountCurrency')}
                        options={[
                          {
                            value: 'CZK',
                            label: 'CZK',
                          },
                          {
                            value: 'EUR',
                            label: 'EUR',
                          },
                          {
                            value: 'USD',
                            label: 'USD',
                          },
                        ]}
                        afterChange={() => {
                          if (formikProps.values.currency !== currency) {
                            formikProps.setFieldValue('primary', false);
                          }
                        }}
                        {...disabledProps}
                      />
                    </Grid>

                    <Grid item xs={6} style={{ lineHeight: '36px' }}>
                      {formikProps.values.currency === 'USD' && t('bankAccount_USD_info')}
                    </Grid>
                    {currency === 'EUR' && (
                      <Grid item xs={12}>
                        <strong>{t('bankAccount_primaryCurrencyEUR')}</strong>
                      </Grid>
                    )}
                    {formikProps.values.hasIban ? (
                      <Grid item xs={12}>
                        <FormTextField
                          name="iban"
                          label={t('general_bankAccount_iban')}
                          maxLength={40}
                          {...disabledProps}
                        />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={3}>
                          <FormTextField
                            name="accountPrefix"
                            label={t('general_bankAccount_prefix')}
                            maxLength={6}
                            {...disabledProps}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormTextField
                            name="accountNumber"
                            label={t('general_bankAccount_number')}
                            maxLength={10}
                            {...disabledProps}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <FormSelectField
                            name="bankCode"
                            label={t('general_bankAccount_code')}
                            options={options.czCodes
                              .concat([t('general_bankAccount_skCodes')].concat(options.skCodes))
                              .map((bankCode) => ({
                                value: bankCode === t('general_bankAccount_skCodes') ? '' : bankCode,
                                label: bankCode,
                              }))}
                            {...disabledProps}
                          />
                        </Grid>
                      </>
                    )}
                    {legal ? (
                      <>
                        <Grid item xs={6}>
                          <FormSelectField
                            name="ownerCompanyName"
                            label={t('general_bankAccount_firstname')}
                            options={[{ value: options.ownerCompanyName, label: options.ownerCompanyName }]}
                            empty
                            specificError
                          />
                        </Grid>
                        <Grid item xs={6} />
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <FormSelectField
                            name="ownership"
                            label={t('general_bankAccount_ownership')}
                            options={options.ownership.map((o) => ({
                              value: o,
                              label: t(`bankAccount_ownership_${o}`),
                            }))}
                            afterChange={() => {
                              switch (formikProps.values.ownership) {
                                case BankAccountOwnership.CLIENT:
                                  formikProps.setFieldValue('ownerFirstname', options.ownerFirstname);
                                  formikProps.setFieldValue('ownerLastname', options.ownerLastname);
                                  break;
                                case BankAccountOwnership.JOINED:
                                  formikProps.setFieldValue('ownerFirstname', '');
                                  formikProps.setFieldValue('ownerLastname', '');
                                  break;
                                case BankAccountOwnership.OTHER:
                                  formikProps.setFieldValue('ownerFirstname', '');
                                  formikProps.setFieldValue('ownerLastname', '');
                                  formikProps.setFieldValue('ownership', '');
                                  setAMLinfo(true);
                                  break;
                              }
                            }}
                            {...disabledProps}
                          />
                        </Grid>
                        <Grid item xs={6} />
                        <Grid item xs={6}>
                          <FormTextField
                            name="ownerFirstname"
                            label={t('general_bankAccount_firstname')}
                            specificError
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormTextField name="ownerLastname" label={t('general_bankAccount_lastname')} specificError />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={6}>
                      <Switch
                        name="primary"
                        label={t('bankAccount_primary')}
                        tooltipContent={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t('bankAccount_primary_info2', {
                                interpolation: { escapeValue: false },
                                currency,
                              }),
                            }}
                          />
                        }
                        disabled={formikProps.values.currency !== currency || !!disabledProps}
                      />
                    </Grid>
                  </Grid>
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            nextButtonId="additionalTarget-modal-submit"
            backText={t('general_cancel')}
            onBack={handleClose}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default compose(observer)(BankAccountModal);
